import { useMemo } from "react";

import { TFunction } from "react-i18next";
import * as yup from "yup";
import { ValidationError, AnyObjectSchema } from "yup";

export interface YupHandlers<T> {
  fieldHasError: (field: keyof T) => boolean;
  getErrorByField: (field: keyof T) => string | undefined;
  someFieldHasError: (fields: string[]) => boolean;
}

const useYupValidation = <T = any>(errors: ValidationError[]) => {
  return {
    fieldHasError: (field: keyof T) => {
      return !!errors.find((error) => error.path === field);
    },
    getErrorByField: (field: keyof T) => {
      return errors.find((error) => error.path === field)?.message;
    },
    someFieldHasError: (fields: string[]) => {
      return !!errors.find((error) => error.path && fields.includes(error.path));
    },
  };
};

export const useYupErrors = <T>(errors: ValidationError[]): YupHandlers<T> => {
  return {
    fieldHasError: (field: keyof T) => {
      return !!errors.find((error) => error.path === field);
    },
    getErrorByField: (field: keyof T) => {
      return errors.find((error) => error.path === field)?.message;
    },
    someFieldHasError: (fields: string[]) => {
      return !!errors.find((error) => error.path && fields.includes(error.path));
    },
  };
};

export const useYupSchemas = (translate: TFunction<"translation", undefined>) =>
  useMemo(() => {
    return {
      stringRequired: yup
        .string()
        .typeError(translate("thisFieldIsRequired"))
        .required(translate("thisFieldIsRequired")),
      booleanRequired: yup
        .boolean()
        .typeError(translate("thisFieldIsRequired"))
        .required(translate("thisFieldIsRequired")),
      numberRequired: yup
        .number()
        .typeError(translate("thisFieldIsRequired"))
        .required(translate("thisFieldIsRequired")),
      objectRequired: yup
        .object()
        .typeError(translate("thisFieldIsRequired"))
        .required(translate("thisFieldIsRequired")),
      arrayRequired: yup
        .array()
        .typeError("thisFieldIsRequired")
        .required(translate("thisFieldIsRequired"))
        .min(1, translate("thisFieldIsRequired")),
      stringNullable: yup.string().nullable(),
      booleanNullable: yup.boolean().nullable(),
      numberNullable: yup.number().nullable(),
      objectNullable: yup.object().nullable(),
    };
  }, []);

export const useHandleValidate = (
  value: any,
  schema: AnyObjectSchema,
  setErrors: (errors: ValidationError[]) => void
): boolean => {
  try {
    schema.validateSync(value, { abortEarly: false });
    setErrors([]);
    return true;
  } catch (error: any) {
    if (error instanceof yup.ValidationError) {
      setErrors(error?.inner);
    }

    return false;
  }
};

export const checkFieldsHasError = (callback: (field: string) => boolean, ...keys: string[]) => {
  return keys.some((key) => callback(key));
};

export default useYupValidation;
